import { computed, IComputedValue, reaction } from 'mobx';
import { useObserver } from 'mobx-react';
import { useEffect } from 'react';
import { intl, translate } from '../../../../../../common/intl';
import { DocumentCategory, DocumentRevisionStatus } from '../../../../../../state/ducks/documentRevisions/types';
import FBAutocompleteAsyncState from '../../../../FBAutocompleteAsync/FBAutocompleteAsync.state';
import FBAutocompleteAsyncStore from '../../../../FBAutocompleteAsync/FBAutocompleteAsync.store';
import { FBAutocompleteAsyncOption } from '../../../../FBAutocompleteAsync/FBAutocompleteAsync.types';
import { FB } from '../../../../helpers';
import { customEQOptionType, EquipOption } from '../column.templates/types';

const isValidDate = (date?: string): boolean => Boolean(date && !Number.isNaN(Date.parse(date)));

const parseEquipmentOption = (equipment: EquipOption): customEQOptionType => {
  const { id, name, eqStatus, displayRevision, document, precalc, status } = equipment;
  const caliDueDate = precalc?.cali_due_date;
  const pmDueDate = precalc?.pm_due_date;
  const validCalDate = isValidDate(caliDueDate) ? caliDueDate : '';
  const validPmDate = isValidDate(pmDueDate) ? pmDueDate : '';

  return {
    label: translate('common.select.revision', {
      docId: document?.docId,
      displayRevision,
      name,
    }),
    complianceDueDate: validCalDate ?? validPmDate ?? '',
    calDueDate: validCalDate,
    pmDueDate: validPmDate,
    status: eqStatus,
    value: id,
    docStatus: status,
    category: document.documentType.category,
  };
};

interface EquipmentFetchData {
  equipmentOptions: customEQOptionType[]
  allowedEditEqOptions: customEQOptionType[]
  releasedEquipmentOptions: customEQOptionType[]
  filterEquipmentOptions: customEQOptionType[]
}

const EMPTY_EQUIPMENT_OPTIONS: EquipmentFetchData = {
  equipmentOptions: [],
  allowedEditEqOptions: [],
  releasedEquipmentOptions: [],
  filterEquipmentOptions: [],
};

const equipmentOptionsCache = new WeakMap<Map<string, EquipOption>, Map<string, IComputedValue<EquipmentFetchData>>>();

const getEquipmentOptions = (asyncData?: Map<string, EquipOption>): EquipmentFetchData => {
  if (!asyncData) {
    return EMPTY_EQUIPMENT_OPTIONS;
  }

  let locales = equipmentOptionsCache.get(asyncData);
  if (!locales) {
    locales = new Map();
    equipmentOptionsCache.set(asyncData, locales);
  }
  let options = locales.get(intl.locale);
  if (!options) {
    options = computed(() => {
      const equipmentOptions = Array.from(asyncData.values()).map(parseEquipmentOption);
      return {
        equipmentOptions,
        allowedEditEqOptions: equipmentOptions.filter(
          (option) => option.docStatus !== DocumentRevisionStatus.Canceled,
        ),
        filterEquipmentOptions: equipmentOptions.filter(
          (option) => option.category === DocumentCategory.Equipment
            && option.docStatus === DocumentRevisionStatus.Released,
        ),
        releasedEquipmentOptions: equipmentOptions.filter(
          (option) => option.docStatus === DocumentRevisionStatus.Released,
        ),
      };
    });
    locales.set(intl.locale, options);
  }
  return options.get();
};

const parsedEquipmentOptionCache = new WeakMap<EquipOption, { locale: string, option: customEQOptionType }>();

export const getEquipmentOption = (id?: string): customEQOptionType | undefined => {
  if (!id) {
    return undefined;
  }

  const record = FBAutocompleteAsyncStore.getValue<EquipOption>(
    FBAutocompleteAsyncOption.equipment,
    id,
  );

  if (!record) {
    return undefined;
  }

  const cached = parsedEquipmentOptionCache.get(record);
  if (cached?.locale === intl.locale) {
    return cached.option;
  }

  const parsed = parseEquipmentOption(record);
  parsedEquipmentOptionCache.set(record, { locale: intl.locale, option: parsed });
  return parsed;
};

export const useFetchData = () => {
  const optionId = FBAutocompleteAsyncOption.equipment;
  const equipAsync = FB.useRef<FBAutocompleteAsyncState>(FBAutocompleteAsyncState, {
    optionId,
  });

  useEffect(() => {
    equipAsync.load();
  }, [equipAsync]);

  return useObserver(() => getEquipmentOptions(
    FBAutocompleteAsyncStore.data.get(optionId) as Map<string, EquipOption> | undefined,
  ));
};

interface EquipmentSpecFetchData {
  equipmentSpcOptions: customEQOptionType[]
  allowedEditEqSpecOptions: customEQOptionType[]
}

const EMPTY_EQUIPMENT_SPEC_OPTIONS: EquipmentSpecFetchData = {
  equipmentSpcOptions: [],
  allowedEditEqSpecOptions: [],
};

const equipmentSpecOptionsCache = new WeakMap<Map<string, EquipOption>, Map<string, IComputedValue<EquipmentSpecFetchData>>>();

const getEquipmentSpecOptions = (
  asyncData?: Map<string, EquipOption>,
): EquipmentSpecFetchData => {
  if (!asyncData) {
    return EMPTY_EQUIPMENT_SPEC_OPTIONS;
  }

  let locales = equipmentSpecOptionsCache.get(asyncData);
  if (!locales) {
    locales = new Map();
    equipmentSpecOptionsCache.set(asyncData, locales);
  }
  let options = locales.get(intl.locale);
  if (!options) {
    options = computed(() => {
      const equipmentSpcOptions = Array.from(asyncData.values()).map(parseEquipmentOption);
      return {
        equipmentSpcOptions,
        allowedEditEqSpecOptions: equipmentSpcOptions.filter(
          (option) => option.docStatus !== DocumentRevisionStatus.Canceled,
        ),
      };
    });
    locales.set(intl.locale, options);
  }
  return options.get();
};

export const useEquipmentOptions = (id: string) => {
  const baseOptionId = FBAutocompleteAsyncOption.equipmentSpecifications;
  // Create a unique key by combining optionId with id to avoid conflicts between different equipment specs
  const uniqueOptionId = id ? `${baseOptionId}_${id}` : baseOptionId;
  const autocompleteAsyncState = FB.useRef<FBAutocompleteAsyncState>(FBAutocompleteAsyncState, { optionId: baseOptionId });

  useEffect(() => {
    if (!id) {
      return;
    }

    // Check if data is already in store, if so, skip API call
    const existingData = FBAutocompleteAsyncStore.data.get(uniqueOptionId);
    if (existingData) {
      return;
    }

    const dispose = reaction(
      () => autocompleteAsyncState.autocompleteValueApi.data,
      (apiData) => {
        if (apiData) {
          const preparedData = Array.isArray(apiData) ? apiData : [apiData];
          FBAutocompleteAsyncStore.setOptionData(uniqueOptionId, preparedData);
        }
      },
    );

    autocompleteAsyncState.setOptionId(baseOptionId);
    autocompleteAsyncState.fetchAutocompleteOption(id, baseOptionId);

    return dispose;
  }, [autocompleteAsyncState, id, baseOptionId, uniqueOptionId]);

  return useObserver(() => {
    if (!id) {
      return EMPTY_EQUIPMENT_SPEC_OPTIONS;
    }

    const asyncData = FBAutocompleteAsyncStore.data.get(uniqueOptionId) as Map<string, EquipOption>;
    return getEquipmentSpecOptions(asyncData);
  });
};
